import {
  parseLiveEvent,
  sessionCloseCommand,
} from "@workspace/voice-shared/live";
import type { LiveCallDataChannel, LiveCallPeerConnection } from "./platform";

const ICE_GATHER_TIMEOUT_MS = 3000;
const CLOSE_GRACE_MS = 1500;

export function setRemoteAudio(pc: LiveCallPeerConnection, enabled: boolean) {
  for (const receiver of pc.getReceivers()) {
    if (receiver.track) {
      receiver.track.enabled = enabled;
    }
  }
}

/** Closes the session (gracefully unless told not to), then the pc. */
export function closeLeg(
  pc: LiveCallPeerConnection,
  dc: LiveCallDataChannel | null,
  graceful = true,
  onClosed?: () => void
) {
  pc.onconnectionstatechange = null;
  pc.ontrack = null;
  setRemoteAudio(pc, false);
  if (!graceful || dc?.readyState !== "open") {
    if (dc) {
      dc.onmessage = null;
      dc.onclose = null;
    }
    pc.close();
    onClosed?.();
    return;
  }
  let done = false;
  const release = () => {
    if (done) {
      return;
    }
    done = true;
    clearTimeout(timer);
    dc.onmessage = null;
    dc.onclose = null;
    pc.close();
    onClosed?.();
  };
  const timer = setTimeout(release, CLOSE_GRACE_MS);
  dc.onmessage = (ev) => {
    if (parseLiveEvent(ev.data)?.type === "session.closed") {
      release();
    }
  };
  dc.onclose = release;
  dc.send(JSON.stringify(sessionCloseCommand()));
}

export function waitForIceGathering(pc: LiveCallPeerConnection): Promise<void> {
  if (pc.iceGatheringState === "complete") {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      pc.removeEventListener("icegatheringstatechange", onChange);
      resolve();
    };
    const onChange = () => {
      if (pc.iceGatheringState === "complete") {
        done();
      }
    };
    const timer = setTimeout(done, ICE_GATHER_TIMEOUT_MS);
    pc.addEventListener("icegatheringstatechange", onChange);
  });
}

export function timeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}
